import {colors} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import {InlineWidget as CalendlyInlineWidget, useCalendlyEventListener} from 'react-calendly'
import styled from 'styled-components'

import Header from '@/components/header'
import * as tracking from '@/components/lead-form/tracking'
import SEO from '@/components/seo'
import type {CalendlyPageQuery} from '@/graphql-types'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import {Main} from '@helios/shared/components/skip-to-content'
import useNavigate from '@helios/shared/hooks/use-navigate'
import {useSessionStorage} from '@helios/shared/hooks/use-storage'
import type {CountryCode, LanguageCode} from '@helios/shared/locale/locales'
import locales from '@helios/shared/locale/locales'
import {container} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import CompanySize from '@helios/shared/types/company-size'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'

const isValidCompanySize = (size?: string): size is CompanySize =>
    !!size && Object.values(CompanySize).some((companySize) => companySize === size)

const isValidCountry = (country?: string): country is CountryCode =>
    !!country && locales.some((locale) => locale.country === country)

const isValidLanguage = (language?: string): language is LanguageCode =>
    !!language && locales.some((locale) => locale.language === language)

interface CalendlyParams {
    source?: 'demo-form'
    name?: string
    email?: string
    phoneNumber?: string
    country?: CountryCode
    language?: LanguageCode
    companySize?: CompanySize
}

type Props = PageProps<CalendlyPageQuery>
type CalendlyLinksNode = Props['data']['allPrismicCalendlyPage']['nodes']

/**
 * Gets all the links from Prismic and returns the specific one for the provided country and company size
 */
const getCalendlyLink = (
    data: CalendlyLinksNode,
    country: CountryCode,
    companySize: CompanySize
) => {
    const linksGroup = data.find((node) => node.lang.split('-')[1] === country)?.data
        .redirect_links[0]

    const mappedLinks = {
        [CompanySize['Size_1']]: linksGroup?.company_size_1.url,
        [CompanySize['Size_2']]: linksGroup?.company_size_2.url,
        [CompanySize['Size_5']]: linksGroup?.company_size_5.url,
        [CompanySize['Size_10']]: linksGroup?.company_size_10.url,
        [CompanySize['Size_50']]: linksGroup?.company_size_50.url,
        [CompanySize['Size_100']]: linksGroup?.company_size_100.url,
        [CompanySize['Size_250']]: linksGroup?.company_size_250.url,
        [CompanySize['Size_500']]: linksGroup?.company_size_500.url,
        [CompanySize['SoleTrader']]: linksGroup?.sole_trader.url,
    }

    return mappedLinks[companySize]
}

const CalendlyPage = ({data}: Props) => {
    const pageData = data.prismicCalendlyPage.data
    const [calendlyURL, setCalendlyURL] = React.useState<string>('')
    const [sessionValue] = useSessionStorage<CalendlyParams>('calendlyParams', {})
    const navigate = useNavigate()

    React.useEffect(() => {
        const {source, country, companySize, language} = sessionValue

        if (
            source === 'demo-form' &&
            isValidCompanySize(companySize) &&
            isValidCountry(country) &&
            isValidLanguage(language)
        ) {
            const calendlyLink = getCalendlyLink(
                data.allPrismicCalendlyPage.nodes,
                country,
                companySize
            )

            if (calendlyLink) {
                setCalendlyURL(calendlyLink)
                tracking.pleoDemoReached({
                    country,
                    companySize,
                })
            }
        } else {
            navigate('/demo')
        }
    }, [navigate, sessionValue, data.allPrismicCalendlyPage.nodes])

    useCalendlyEventListener({
        onDateAndTimeSelected: () => {
            tracking.demoBookingTimeSelected()
        },
        onEventScheduled: () => {
            tracking.pleoDemoBooked({
                country: sessionValue.country,
                companySize: sessionValue.companySize,
            })
            tracking.googleAdsConversion({
                country: sessionValue.country,
                companySize: sessionValue.companySize,
            })
        },
    })

    if (!pageData) {
        return handleNoPrismicData()
    }

    data.allPrismicCalendlyPage.nodes?.forEach((documentsByLocale) => {
        if (
            Object.values(documentsByLocale.data.redirect_links[0]).some(
                (link) => !link.url.startsWith('https://calendly.com')
            )
        ) {
            throw new Error(`Invalid Calendly link`)
        }
    })

    return (
        <Wrapper>
            <HeaderWrapper>
                <Header hideLeftNav hideRightNav />
            </HeaderWrapper>

            <Main>
                {!!pageData.title && (
                    <TitleWrapper>
                        <Text as="h3" variant="title-base" center>
                            {pageData.title}
                        </Text>
                    </TitleWrapper>
                )}

                {!!calendlyURL && (
                    <CalendlyInlineWidget
                        prefill={{
                            name: sessionValue.name,
                            email: sessionValue.email,
                            customAnswers: {
                                a1: sessionValue.phoneNumber,
                            },
                        }}
                        url={`${calendlyURL}?hide_gdpr_banner=1`}
                        styles={{
                            height: 1200,
                        }}
                    />
                )}
            </Main>
        </Wrapper>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<CalendlyPageQuery>) => {
    const pageData = data.prismicCalendlyPage.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

const Wrapper = styled.div`
    min-height: 100vh;
    padding-bottom: 80px;

    .calendly-inline-widget iframe {
        /* Needed to prevent slight nested scroll due to default inline display  */
        display: block;
    }

    /* We need to match Calendly's breakpoints here and below */
    @media (min-width: 650px) {
        padding-bottom: 0;
        background: ${colors.background.muted};
    }
`

const HeaderWrapper = styled.div`
    header {
        @media (min-width: 650px) {
            background: transparent;
        }
    }
`

const TitleWrapper = styled.div`
    ${container}
    display: none;

    @media (min-width: 650px) {
        display: block;
    }
`

export const query = graphql`
    query CalendlyPage($locale: String!) {
        prismicCalendlyPage(lang: {eq: $locale}) {
            _previewable
            data {
                meta_title
                meta_description
                open_graph_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                title
            }
        }
        allPrismicCalendlyPage {
            nodes {
                lang
                data {
                    redirect_links {
                        sole_trader {
                            ...PrismicLink
                        }
                        company_size_1 {
                            ...PrismicLink
                        }
                        company_size_2 {
                            ...PrismicLink
                        }
                        company_size_5 {
                            ...PrismicLink
                        }
                        company_size_10 {
                            ...PrismicLink
                        }
                        company_size_50 {
                            ...PrismicLink
                        }
                        company_size_100 {
                            ...PrismicLink
                        }
                        company_size_250 {
                            ...PrismicLink
                        }
                        company_size_500 {
                            ...PrismicLink
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(CalendlyPage)
